/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import PropTypes from 'prop-types';
import { Children, controllable } from '@instructure/ui-prop-types';
import { PositionPropTypes } from '@instructure/ui-position';
import { DrilldownPage } from './DrilldownPage';
const propTypes = {
  rootPageId: PropTypes.string.isRequired,
  children: Children.oneOf([DrilldownPage]),
  id: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  rotateFocus: PropTypes.bool,
  as: PropTypes.elementType,
  role: PropTypes.string,
  elementRef: PropTypes.func,
  drilldownRef: PropTypes.func,
  overflowX: PropTypes.oneOf(['auto', 'hidden', 'visible']),
  overflowY: PropTypes.oneOf(['auto', 'hidden', 'visible']),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  trigger: PropTypes.node,
  placement: PositionPropTypes.placement,
  defaultShow: PropTypes.bool,
  show: controllable(PropTypes.bool, 'onToggle', 'defaultShow'),
  onToggle: PropTypes.func,
  onSelect: PropTypes.func,
  onDismiss: PropTypes.func,
  onFocus: PropTypes.func,
  onMouseOver: PropTypes.func,
  popoverRef: PropTypes.func,
  mountNode: PositionPropTypes.mountNode,
  positionTarget: PositionPropTypes.mountNode,
  positionContainerDisplay: PropTypes.oneOf(['inline-block', 'block']),
  constrain: PositionPropTypes.constrain,
  shouldHideOnSelect: PropTypes.bool,
  shouldContainFocus: PropTypes.bool,
  shouldReturnFocus: PropTypes.bool,
  withArrow: PropTypes.bool,
  offsetX: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  offsetY: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  shouldSetAriaExpanded: PropTypes.bool
};
const allowedProps = ['rootPageId', 'children', 'id', 'label', 'disabled', 'rotateFocus', 'as', 'role', 'overflowX', 'overflowY', 'height', 'width', 'minHeight', 'minWidth', 'maxHeight', 'maxWidth',
// Popover version props
'trigger', 'placement', 'defaultShow', 'show', 'onToggle', 'onSelect', 'onDismiss', 'onFocus', 'onMouseOver', 'elementRef', 'drilldownRef', 'popoverRef', 'mountNode', 'positionTarget', 'positionContainerDisplay', 'constrain', 'shouldHideOnSelect', 'shouldContainFocus', 'shouldReturnFocus', 'withArrow', 'offsetX', 'offsetY'];
export { propTypes, allowedProps };